import { Injectable } from '@angular/core';
import _ from 'lodash';

abstract class TemperatureConverter {
  public abstract toCelsius(value: number): number;
  public abstract toFahrenheit(value: number): number;
  public abstract toKelvin(value: number): number;
  public abstract getUnit(): string;

  public convert(value: number, unit: string): number {
    switch (unit) {
      default:
      case TemperatureConverterService.TEMPERATURES.CELSIUS:
        return this.toCelsius(value);

      case TemperatureConverterService.TEMPERATURES.FAHRENHEIT:
        return this.toFahrenheit(value);

      case TemperatureConverterService.TEMPERATURES.KELVIN:
        return this.toKelvin(value);
    }
  }
}

class CelsiusConverter extends TemperatureConverter {
  public toCelsius(celsius: number): number {
    return celsius;
  }

  public toFahrenheit(celsius: number): number {
    return celsius * (9 / 5) + 32;
  }

  public toKelvin(celsius: number): number {
    return Number(celsius) + 273.15;
  }

  public getUnit(): string {
    return String.fromCharCode(186) + ' C';
  }
}

class FahrenheitConverter extends TemperatureConverter {
  public toCelsius(fahrenheit: number): number {
    return (fahrenheit - 32) * (5 / 9);
  }

  public toFahrenheit(fahrenheit: number): number {
    return fahrenheit;
  }

  public toKelvin(fahrenheit: number): number {
    return (fahrenheit + 459.67) * (5 / 9);
  }

  public getUnit(): string {
    return String.fromCharCode(186) + ' F';
  }
}

class KelvinConverter extends TemperatureConverter {
  public toCelsius(kelvin: number): number {
    return kelvin - 273.15;
  }

  public toFahrenheit(kelvin: number): number {
    return kelvin * (9 / 5) - 459.67;
  }

  public toKelvin(kelvin: number): number {
    return kelvin;
  }

  public getUnit(): string {
    return String.fromCharCode(186) + ' K';
  }
}

class TemperatureConverterFactory {
  public static getConverterFactory(type: string): TemperatureConverter {
    switch (type) {
      default:
      case TemperatureConverterService.TEMPERATURES.CELSIUS:
        return new CelsiusConverter();

      case TemperatureConverterService.TEMPERATURES.FAHRENHEIT:
        return new FahrenheitConverter();

      case TemperatureConverterService.TEMPERATURES.KELVIN:
        return new KelvinConverter();
    }
  }
}

@Injectable()
export class TemperatureConverterService {
  public static readonly TEMPERATURES = {
    CELSIUS: 'C',
    FAHRENHEIT: 'F',
    KELVIN: 'K'
  };
  public static readonly DEFAULT_TEMPERATURE = TemperatureConverterService
    .TEMPERATURES.CELSIUS;

  private temperature: string = TemperatureConverterService.DEFAULT_TEMPERATURE;

  /**
   * Set the temperature for conversions
   * @param value New temperature
   */
  public setTemperature(value: string) {
    this.temperature =
      value &&
      _.values(TemperatureConverterService.TEMPERATURES).indexOf(value) > -1
        ? value
        : TemperatureConverterService.DEFAULT_TEMPERATURE;
  }

  /**
   * Convert from one temperature unit to another
   * @param value Temperature value to convert
   * @param unit Temperature unit of the value
   */
  public convert(
    value: number,
    unit: string = TemperatureConverterService.DEFAULT_TEMPERATURE
  ) {
    if (value == null) {
      return 0;
    }

    unit =
      unit &&
      _.values(TemperatureConverterService.TEMPERATURES).indexOf(unit) > -1
        ? unit
        : TemperatureConverterService.DEFAULT_TEMPERATURE;

    return TemperatureConverterFactory.getConverterFactory(unit).convert(
      value,
      this.temperature
    );
  }

  /**
   * Get unit symbol of the current temperature unit
   */
  public getUnit() {
    return TemperatureConverterFactory.getConverterFactory(
      this.temperature
    ).getUnit();
  }
}
